import { parse, isValid, isBefore, isAfter, startOfDay, endOfDay } from 'date-fns';
import { clock } from './clock';
// DTC-E: optional shop-tz path for `validateTicketStartTime`. When the
// caller supplies `shopTimezone`, `startTime` is interpreted as a UTC
// instant and converted to shop-local minutes-since-midnight via the
// canonical helper. When omitted, the pre-DTC-E `toLocaleTimeString`
// browser-local fallback is preserved verbatim — same-TZ behavior is
// unchanged for callers that have not threaded the timezone through.
import { getMinutesInShopTZ } from './timezoneMath';

/**
 * Validates that a ticket's start time falls within reasonable business hours
 * Prevents ghost tickets with invalid times like 3:41 AM
 *
 * DTC-E: pass `shopTimezone` to compare a UTC `startTime` against the
 * shop's wall-clock open/close. When omitted, falls back to browser-
 * local hours (pre-DTC-E behavior; correct only when browser TZ matches
 * shop TZ).
 */
export const validateTicketStartTime = (
  startTime: Date,
  shopHours?: { open: string; close: string },
  shopTimezone?: string,
): {
  isValid: boolean;
  error?: string;
} => {
  let startTimeMinutes: number;
  let timeString: string;
  if (shopTimezone) {
    // Canonical path: convert UTC startTime to shop-local minutes.
    startTimeMinutes = getMinutesInShopTZ(startTime.toISOString(), shopTimezone);
    const hh = Math.floor(startTimeMinutes / 60);
    const mm = startTimeMinutes % 60;
    timeString = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  } else {
    // Legacy fallback: browser-local hours (pre-DTC-E behavior).
    timeString = startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    const [hours, minutes] = timeString.split(':').map(Number);
    startTimeMinutes = hours * 60 + minutes;
  }
  
  // Default business hours: 7 AM to 7 PM
  const defaultOpenMinutes = 7 * 60;
  const defaultCloseMinutes = 19 * 60;
  
  let openMinutes = defaultOpenMinutes;
  let closeMinutes = defaultCloseMinutes;
  
  // CRITICAL FIX: Parse shop hours with minutes for accurate validation
  if (shopHours && shopHours.open && shopHours.close) {
    const openParsed = parse(shopHours.open, 'HH:mm', new Date());
    const closeParsed = parse(shopHours.close, 'HH:mm', new Date());
    
    if (isValid(openParsed)) {
      openMinutes = openParsed.getHours() * 60 + openParsed.getMinutes();
    }
    if (isValid(closeParsed)) {
      closeMinutes = closeParsed.getHours() * 60 + closeParsed.getMinutes();
    }
  }
  
  // Check if time is unreasonably early (before shop opens)
  if (startTimeMinutes < openMinutes) {
    const openHour = Math.floor(openMinutes / 60);
    const openMin = openMinutes % 60;
    return {
      isValid: false,
      error: `Start time ${timeString} is before shop opens at ${String(openHour).padStart(2, '0')}:${String(openMin).padStart(2, '0')}`
    };
  }
  
  // Check if time is unreasonably late (after shop closes)
  if (startTimeMinutes >= closeMinutes) {
    const closeHour = Math.floor(closeMinutes / 60);
    const closeMin = closeMinutes % 60;
    return {
      isValid: false,
      error: `Start time ${timeString} is after shop closes at ${String(closeHour).padStart(2, '0')}:${String(closeMin).padStart(2, '0')}`
    };
  }
  
  return { isValid: true };
};

/**
 * Validates that a ticket has all required fields and logical data
 */
export const validateTicketData = (ticketData: any): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];
  
  // Validate required fields
  if (!ticketData.vehicle?.make) errors.push('Vehicle make is required');
  if (!ticketData.vehicle?.model) errors.push('Vehicle model is required');
  if (!ticketData.vehicle?.year) errors.push('Vehicle year is required');
  if (!ticketData.type) errors.push('Ticket type is required');
  if (!ticketData.jobs || ticketData.jobs.length === 0) errors.push('At least one job is required');
  
  // Validate start time if provided
  if (ticketData.startTime) {
    const startDate = new Date(ticketData.startTime);
    if (!isValid(startDate)) {
      errors.push('Invalid start time');
    } else {
      // Use clock.now() for time comparisons in validation
      const validation = validateTicketStartTime(startDate);
      if (!validation.isValid && validation.error) {
        errors.push(validation.error);
      }
    }
  }
  
  // Validate predicted minutes
  if (!ticketData.predictedMinutes || ticketData.predictedMinutes <= 0) {
    errors.push('Predicted minutes must be greater than 0');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
